// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pure rules behind the corner's "I'll remember that" card (CornerMemory.ts): reading the
// gateway signal, the queue of saved notes (newest first, each id once), what one card lists and
// when the card can be drawn. Nothing goes into memory silently, so every note gets a card.

/** A note the assistant saved about me (its `remember` tool), as the card shows it. */
export interface RememberedNote {
	id: string;
	text: string;
	topic: string | null;
	/** When it was saved (ms). */
	at: number;
}

/** The card closes by itself after this long unless the pointer or focus is on it. */
export const MEMORY_AUTO_CLOSE_MS = 12_000;
/** One card lists at most this many notes; the rest are "+N more". */
export const MEMORY_CARD_ROWS = 3;
/** Notes kept waiting for a card at most (the oldest go first; they are all in Settings anyway). */
export const MEMORY_QUEUE_LIMIT = 20;
/** Within this long of saving, the card says "now"; after that it gives the time. */
export const MEMORY_NOW_MS = 2 * 60_000;

/**
 * KONVO_ASSISTANT_ACTIVITY `{memory: {id, text, topic}, at}`: the saved note, or null when the event
 * is something else (a tool label or heartbeat) or the note is unusable.
 */
export function parseMemoryEvent(data: unknown, now: number): RememberedNote | null {
	if (!data || typeof data !== 'object') return null;
	const memory = (data as {memory?: unknown}).memory;
	if (!memory || typeof memory !== 'object') return null;
	const {id, text, topic} = memory as {id?: unknown; text?: unknown; topic?: unknown};
	if (typeof id !== 'string' || !id || typeof text !== 'string' || !text.trim()) return null;
	const raw = (data as {at?: unknown}).at;
	const parsed = typeof raw === 'string' ? Date.parse(raw) : Number.NaN;
	// A clock far off on either side (or no time at all) reads as "now".
	const at = Number.isFinite(parsed) && Math.abs(parsed - now) < 24 * 60 * 60_000 ? Math.min(parsed, now) : now;
	return {id, text: text.trim(), topic: typeof topic === 'string' && topic ? topic : null, at};
}

/**
 * Put a note on top of the list (newest first). A note already seen (the same push twice, or one
 * already on the card) is left out; the list is capped at `limit`.
 */
export function addNote(
	notes: ReadonlyArray<RememberedNote>,
	note: RememberedNote,
	seen: ReadonlySet<string>,
	limit = MEMORY_QUEUE_LIMIT,
): Array<RememberedNote> {
	if (seen.has(note.id) || notes.some((entry) => entry.id === note.id)) return [...notes];
	return [note, ...notes].slice(0, limit);
}

/** Notes waiting plus notes already on the card: one list, newest first, each id once. */
export function mergeNotes(
	first: ReadonlyArray<RememberedNote>,
	second: ReadonlyArray<RememberedNote>,
	limit = MEMORY_QUEUE_LIMIT,
): Array<RememberedNote> {
	const byId = new Map<string, RememberedNote>();
	for (const note of [...first, ...second]) if (!byId.has(note.id)) byId.set(note.id, note);
	return [...byId.values()].sort((a, b) => b.at - a.at).slice(0, limit);
}

export function removeNote(notes: ReadonlyArray<RememberedNote>, id: string): Array<RememberedNote> {
	return notes.filter((note) => note.id !== id);
}

/** What one card lists: the newest few, and how many more there are. */
export function cardRows(
	notes: ReadonlyArray<RememberedNote>,
	max = MEMORY_CARD_ROWS,
): {rows: Array<RememberedNote>; more: number} {
	return {rows: notes.slice(0, max), more: Math.max(0, notes.length - max)};
}

export interface MemoryDrawConditions {
	phone: boolean;
	panelOpen: boolean;
	modalOpen: boolean;
	/** Desktop: Home's pop-up fills the corner. */
	homePeekOpen: boolean;
	/** A corner suggestion card is on screen: the note waits for it. */
	suggestionShown: boolean;
	/** Settings › What Konvo knows is open: the page shows the note itself. */
	knowsPageOpen: boolean;
	phoneBar: boolean;
	railPanelOpen: boolean;
	keyboardOpen: boolean;
}

/**
 * The card can be drawn now. Unlike suggestions it also shows in the Konvo DM (where the note is
 * usually saved), without the icon there; it waits for the panel, a modal, Home's pop-up and a
 * suggestion card that is up. On phones it rises above the bottom bar like the corner pop-ups.
 */
export function canDrawMemory(conditions: MemoryDrawConditions): boolean {
	if (conditions.knowsPageOpen || conditions.suggestionShown) return false;
	if (conditions.panelOpen || conditions.modalOpen) return false;
	if (conditions.phone) return conditions.phoneBar && !conditions.railPanelOpen && !conditions.keyboardOpen;
	return !conditions.homePeekOpen;
}
